import React, { forwardRef } from 'react';
import { Typeahead, Menu, MenuItem, RenderMenuProps, TypeaheadRef } from 'react-bootstrap-typeahead';
import ReactHtmlParser from 'react-html-parser';
import { Option as TypeaheadOption, FilterByCallback } from 'react-bootstrap-typeahead/types/types';
import _ from 'lodash';

type Option = TypeaheadOption;

interface CustomTypeAheadProps {
  name: string;
  options: Option[];
  onChange: (option: Option | null) => void;
  filterBy: FilterByCallback;
  disabled?: boolean;
  clearButton?: boolean;
  defaultSelected?: Option[];
  selectedItems?: Option[];
  placeholder?: string;
  size?: 'sm' | 'lg';
  isInvalid?: boolean;
  allowNew?: boolean;
  customKey?: string;
  className?: string;
  labelKey?: string;
  menuItemLabelKey?: string;
  customRenderMenuItem?: (results: Option[], menuProps: RenderMenuProps) => React.ReactNode;
  paginate?: boolean;
  newSelectionPrefix?: string;
}

const CustomTypeAhead = forwardRef<TypeaheadRef, CustomTypeAheadProps>(
  (
    {
      name,
      options = [],
      onChange,
      filterBy,
      disabled = false,
      clearButton = false,
      defaultSelected = [],
      selectedItems,
      placeholder = 'Select an option...',
      size,
      isInvalid = false,
      allowNew = false,
      customKey,
      className = 'w-100',
      labelKey = 'label',
      menuItemLabelKey = 'label',
      customRenderMenuItem,
      newSelectionPrefix = '',
    },
    ref
  ) => {
    const renderDefaultMenu = (results: Option[], menuProps: RenderMenuProps): React.ReactElement => {
      const remainingItems = options.filter((opt) => !results.includes(opt));
      const omitedProps = _.omit(menuProps, [
        'newSelectionPrefix',
        'paginationText',
        'renderMenuItemChildren',
      ]);

      const renderMenuItems = (list: Option[], startIndex: number) =>
        list.map((option, index) => {
          const label =
            typeof option === 'string'
              ? option
              : (option[menuItemLabelKey] as string) || (option['label'] as string) || '';

          return (
            <MenuItem
              key={`${menuProps.id}-item-${index + startIndex}`}
              data-test-id={`${index + startIndex}-${label}`}
              option={option}
              position={index + startIndex}
              className={className}
            >
              {ReactHtmlParser(label)}
            </MenuItem>
          );
        });

      return (
        <Menu {...omitedProps}>
          {results.length > 0 ? (
            renderMenuItems(results, 0)
          ) : (
            <div className="px-2 py-1 text-center small">
              <span className="fw-bold text-color">No matches found</span>
            </div>
          )}

          {remainingItems.length > 0 && (
            <>
              <div className="instrument-custom-dropdown px-2 py-1">
                <span>More Options</span>
              </div>
              {renderMenuItems(remainingItems, results.length)}
            </>
          )}
        </Menu>
      );
    };

    const safeProps = {
      options,
      onChange,
      filterBy,
      disabled,
      clearButton,
      placeholder,
      size,
      isInvalid,
      allowNew,
      id: name,
      className,
      labelKey,
      newSelectionPrefix,
      inputProps: {
        id: name,
        name,
      },
    };

    return (
      <Typeahead
        {...safeProps}
        {...(selectedItems ? { selected: selectedItems } : { defaultSelected })}
        ref={ref}
        key={customKey}
        renderMenu={
          customRenderMenuItem
            ? (results, menuProps) => customRenderMenuItem(results, menuProps) as React.ReactElement
            : renderDefaultMenu
        }
        onFocus={(event) => {
          (event.target as HTMLInputElement).select();
        }}
      />
    );
  }
);

CustomTypeAhead.displayName = 'CustomTypeAhead';

export default CustomTypeAhead;
